import { useCallback, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { CRUD_CONFIGS } from "@/lib/crudConfigs";
import { AdminTitle } from "@/components/admin/AdminLayout";

function FieldInput({ f, value, onChange, categories }) {
  const tid = `crud-field-${f.key}`;
  if (f.type === "textarea") return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-xl min-h-[110px]" data-testid={tid} />;
  if (f.type === "switch") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />;
  if (f.type === "category") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="rounded-xl" data-testid={tid}><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
        <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  const type = { number: "number", date: "date", time: "time" }[f.type] || "text";
  return (
    <div className="space-y-2">
      <Input type={type} value={value ?? ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} className="rounded-xl" data-testid={tid} />
      {f.type === "image" && value && <img src={value} alt="" className="h-24 rounded-xl object-cover" />}
    </div>
  );
}

function toPayload(fields, form) {
  const out = { ...form };
  fields.forEach((f) => {
    if (f.type === "number") out[f.key] = out[f.key] === "" || out[f.key] == null ? (f.nullable ? null : 0) : Number(out[f.key]);
  });
  return out;
}

function ItemForm({ cfg, item, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(cfg.fields.map((f) => [f.key, item?.[f.key] ?? f.default])));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const missing = cfg.fields.find((f) => f.required && !form[f.key]);
    if (missing) return toast.error(`${missing.label} zorunlu`);
    setBusy(true);
    try {
      const payload = { ...(item || {}), ...toPayload(cfg.fields, form) };
      if (item) await api.put(`/admin/c/${cfg.api}/${item.id}`, payload);
      else await api.post(`/admin/c/${cfg.api}`, payload);
      toast.success("Kaydedildi");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto rounded-3xl" data-testid="crud-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl font-black text-navy">{item ? `${cfg.singular} düzenle` : `Yeni ${cfg.singular.toLowerCase()}`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {cfg.fields.map((f) => (
            <div key={f.key} className={f.type === "switch" ? "flex items-center justify-between rounded-xl bg-cream px-4 py-3" : ""}>
              <label className="block text-sm font-bold text-navy mb-1.5">{f.label}{f.required && " *"}</label>
              <FieldInput f={f} value={form[f.key]} categories={categories} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
            </div>
          ))}
          <button type="submit" disabled={busy} data-testid="crud-save-btn" className="w-full rounded-full bg-navy py-3 font-heading font-bold text-white hover:bg-navy-dark disabled:opacity-50">
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CrudPage() {
  const { section } = useParams();
  const cfg = CRUD_CONFIGS[section];
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => {
    if (!cfg) return;
    api.get(`/admin/c/${cfg.api}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/c/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [cfg]);
  useEffect(() => {
    load();
  }, [load]);
  if (!cfg) return <Navigate to="/yonetim" replace />;
  const catNames = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const remove = async (it) => {
    if (!window.confirm("Bu kayıt silinecek. Emin misiniz?")) return;
    try {
      await api.delete(`/admin/c/${cfg.api}/${it.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid={`crud-page-${section}`}>
      <AdminTitle title={cfg.title}>
        <button onClick={() => setEditing(null)} data-testid="crud-add-btn" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-dark">
          <Plus className="w-4 h-4" /> Yeni {cfg.singular.toLowerCase()}
        </button>
      </AdminTitle>
      {!items.length && <p className="rounded-2xl bg-white border border-bej p-8 text-center text-slate-500">Henüz kayıt yok.</p>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((it, i) => {
          const d = cfg.display(it, catNames);
          return (
            <div key={it.id} className="flex gap-4 rounded-2xl bg-white border border-bej p-3" data-testid={`crud-item-${i}`}>
              {"image" in d && (d.image ? <img src={d.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" /> :
                <div className="grid place-items-center w-20 h-20 rounded-xl bg-bej-light text-navy/40 shrink-0"><ImageOff className="w-6 h-6" /></div>)}
              <div className="min-w-0 flex-1">
                <p className="font-heading font-extrabold text-navy truncate">{d.title}</p>
                {d.subtitle && <p className="text-sm text-slate-500 truncate">{d.subtitle}</p>}
                <div className="mt-1 flex flex-wrap gap-1">{d.badges.map((b) => <span key={b} className="rounded-full bg-bej px-2 py-0.5 text-[11px] font-bold text-navy">{b}</span>)}</div>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => setEditing(it)} className="inline-flex items-center gap-1 rounded-full bg-bej-light px-3 py-1 text-xs font-bold text-navy hover:bg-bej" data-testid={`crud-edit-${i}`}><Pencil className="w-3 h-3" /> Düzenle</button>
                  <button onClick={() => remove(it)} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700 hover:bg-rose-100" data-testid={`crud-delete-${i}`}><Trash2 className="w-3 h-3" /> Sil</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {editing !== undefined && (
        <ItemForm key={editing?.id || "new"} cfg={cfg} item={editing} categories={categories}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}
